import { z } from "zod";
import type {
  WalletAppleRow,
  WalletPassDesignDocument,
  WalletPassKind,
} from "@lemmonhq/types";
import { walletBindingToken, walletTemplateTokens } from "./bindings";

/**
 * Runtime contract for `wallet_pass_designs.document`.
 *
 * The tRPC router validates saves with it, DBOS parses with it before
 * rendering, and the customer app parses with it before drawing a card. Parsing
 * is strict: unknown keys are rejected, and an invalid document fails loudly
 * instead of being merged over defaults.
 */

const hex = z
  .string()
  .regex(/^#[\da-f]{6}$/i, "Colours must be six-digit hex, e.g. #111827");
const unit = z.number().min(0).max(1);
const key = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/i, "Keys use letters, digits, - and _");

const assetRef = z
  .object({
    asset_id: z.string().min(1),
    fit: z.enum(["cover", "contain"]),
    focal_point: z.object({ x: unit, y: unit }).strict().optional(),
  })
  .strict();

const field = z
  .object({
    key,
    label: z.string().max(40),
    value: z.string().max(500),
    format: z.enum(["text", "number", "date", "currency"]).optional(),
    change_message: z
      .string()
      .max(120)
      .refine((value) => value.includes("%@"), {
        message: "Change messages must contain %@",
      })
      .optional(),
  })
  .strict();

const stamps = z
  .object({
    enabled: z.boolean(),
    target: z.enum(["strip", "poster", "both"]),
    arrangement: z.enum(["row", "grid"]),
    max_per_row: z.number().int().min(1).max(20),
    alignment: z.enum(["left", "center", "right"]),
    overflow: z.enum(["cap", "wrap", "extend"]),
    scrim_opacity: unit,
    caption_enabled: z.boolean(),
  })
  .strict();

const appleRows = z
  .object({
    header: z.array(key).max(3),
    primary: z.array(key).max(2),
    secondary: z.array(key).max(4),
    auxiliary: z.array(key).max(4),
    back: z.array(key).max(20),
  })
  .strict();

const baseDocument = z
  .object({
    brand: z
      .object({
        logo_text: z.string().max(80),
        colors: z
          .object({ background: hex, foreground: hex, label: hex })
          .strict(),
        assets: z
          .object({
            icon: assetRef.optional(),
            logo: assetRef.optional(),
            artwork: assetRef.optional(),
          })
          .strict(),
      })
      .strict(),
    content: z
      .object({
        fields: z.array(field).max(30),
        barcode: z
          .object({
            symbology: z.enum(["qr", "pdf417", "aztec", "code128"]),
            message: z.string().min(1).max(500),
            alt_text: z.string().max(120),
          })
          .strict()
          .nullable(),
        links: z
          .array(
            z
              .object({
                key,
                label: z.string().min(1).max(40),
                url: z.string().min(1).max(500),
              })
              .strict(),
          )
          .max(10),
      })
      .strict(),
    apple: z
      .object({
        enabled: z.boolean(),
        style: z.enum(["storeCard", "eventTicket", "coupon", "generic"]),
        rows: appleRows,
        poster: z
          .object({
            enabled: z.boolean(),
            text_color: hex,
            secondary_text_color: hex,
            scrim_opacity: unit,
          })
          .strict(),
        relevance: z
          .object({
            date: z.string().max(200),
            use_restaurant_location: z.boolean(),
          })
          .strict(),
        sharing_prohibited: z.boolean(),
        grouping_identifier: z.string().max(64),
        suppress_strip_shine: z.boolean(),
      })
      .strict(),
    google: z
      .object({
        enabled: z.boolean(),
        header: key,
        subheader: key.nullable(),
        rows: z.array(z.array(key).min(1).max(3)).max(3),
        modules: z.array(key).max(10),
      })
      .strict(),
    browser: z
      .object({
        enabled: z.boolean(),
        primary: key.nullable(),
        secondary: z.array(key).max(3),
        show_progress: z.boolean(),
      })
      .strict(),
  })
  .strict();

const coinDocument = baseDocument.extend({ stamps }).strict();

const APPLE_ROWS: WalletAppleRow[] = [
  "header",
  "primary",
  "secondary",
  "auxiliary",
  "back",
];

/** Templates a document binds, with the path used in error messages. */
const documentTemplates = (
  document: WalletPassDesignDocument,
): { path: (string | number)[]; template: string }[] => [
  { path: ["brand", "logo_text"], template: document.brand.logo_text },
  ...document.content.fields.flatMap((entry, index) => [
    { path: ["content", "fields", index, "label"], template: entry.label },
    { path: ["content", "fields", index, "value"], template: entry.value },
  ]),
  ...(document.content.barcode
    ? [
        {
          path: ["content", "barcode", "message"],
          template: document.content.barcode.message,
        },
        {
          path: ["content", "barcode", "alt_text"],
          template: document.content.barcode.alt_text,
        },
      ]
    : []),
  ...document.content.links.map((link, index) => ({
    path: ["content", "links", index, "url"],
    template: link.url,
  })),
  {
    path: ["apple", "relevance", "date"],
    template: document.apple.relevance.date,
  },
  {
    path: ["apple", "grouping_identifier"],
    template: document.apple.grouping_identifier,
  },
];

/** Cross-field rules zod's structural checks cannot express. */
const refineDocument =
  (kind: WalletPassKind) =>
  (document: WalletPassDesignDocument, context: z.RefinementCtx) => {
    const keys = new Set<string>();
    document.content.fields.forEach((entry, index) => {
      if (keys.has(entry.key)) {
        context.addIssue({
          code: "custom",
          path: ["content", "fields", index, "key"],
          message: `Duplicate field key "${entry.key}"`,
        });
      }
      keys.add(entry.key);
    });

    const placed = (path: (string | number)[], fieldKey: string | null) => {
      if (fieldKey != null && !keys.has(fieldKey)) {
        context.addIssue({
          code: "custom",
          path,
          message: `Unknown field "${fieldKey}"`,
        });
      }
    };
    for (const row of APPLE_ROWS) {
      document.apple.rows[row].forEach((fieldKey, index) =>
        placed(["apple", "rows", row, index], fieldKey),
      );
    }
    placed(["google", "header"], document.google.header);
    placed(["google", "subheader"], document.google.subheader);
    document.google.rows.forEach((row, rowIndex) =>
      row.forEach((fieldKey, index) =>
        placed(["google", "rows", rowIndex, index], fieldKey),
      ),
    );
    document.google.modules.forEach((fieldKey, index) =>
      placed(["google", "modules", index], fieldKey),
    );
    placed(["browser", "primary"], document.browser.primary);
    document.browser.secondary.forEach((fieldKey, index) =>
      placed(["browser", "secondary", index], fieldKey),
    );

    for (const { path, template } of documentTemplates(document)) {
      for (const token of walletTemplateTokens(template)) {
        if (!walletBindingToken(kind, token)) {
          context.addIssue({
            code: "custom",
            path,
            message: `Unknown binding {{${token}}} for ${kind} passes`,
          });
        }
      }
    }

    const linkKeys = new Set<string>();
    document.content.links.forEach((link, index) => {
      if (linkKeys.has(link.key) || keys.has(link.key)) {
        context.addIssue({
          code: "custom",
          path: ["content", "links", index, "key"],
          message: `Duplicate key "${link.key}"`,
        });
      }
      linkKeys.add(link.key);
    });

    if (
      document.apple.poster.enabled &&
      document.apple.style !== "storeCard" &&
      document.apple.style !== "generic"
    ) {
      context.addIssue({
        code: "custom",
        path: ["apple", "poster", "enabled"],
        message: "The iOS poster layout is only available for store cards",
      });
    }
  };

const schemas = {
  membership: baseDocument.superRefine(refineDocument("membership")),
  reservation: baseDocument.superRefine(refineDocument("reservation")),
  gift_card: baseDocument.superRefine(refineDocument("gift_card")),
  coin: coinDocument.superRefine(refineDocument("coin")),
} satisfies Record<WalletPassKind, z.ZodType>;

/** The validator for one pass kind. */
export const walletPassDesignSchema = (
  kind: WalletPassKind,
): z.ZodType<WalletPassDesignDocument> =>
  schemas[kind] as unknown as z.ZodType<WalletPassDesignDocument>;

/** Structural shape with no kind-specific rules, for tRPC input typing. */
export const walletPassDesignDocumentShape = coinDocument.extend({
  stamps: stamps.optional(),
});

export type WalletDesignIssue = { path: string; message: string };

const formatIssues = (error: z.ZodError): WalletDesignIssue[] =>
  error.issues.map((issue) => ({
    path: issue.path.join("."),
    message: issue.message,
  }));

/** Validates without throwing; the editor shows the issues inline. */
export const validateWalletPassDesign = (
  kind: WalletPassKind,
  candidate: unknown,
):
  | { success: true; document: WalletPassDesignDocument }
  | { success: false; issues: WalletDesignIssue[] } => {
  const result = walletPassDesignSchema(kind).safeParse(candidate);
  return result.success
    ? { success: true, document: result.data }
    : { success: false, issues: formatIssues(result.error) };
};

export class InvalidWalletPassDesignError extends Error {
  readonly issues: WalletDesignIssue[];
  constructor(kind: WalletPassKind, issues: WalletDesignIssue[]) {
    super(
      `Invalid ${kind} wallet pass design: ${issues
        .slice(0, 5)
        .map((issue) => `${issue.path || "document"}: ${issue.message}`)
        .join("; ")}`,
    );
    this.name = "InvalidWalletPassDesignError";
    this.issues = issues;
  }
}

/** Parses or throws `InvalidWalletPassDesignError`. Renderers use this. */
export const parseWalletPassDesign = (
  kind: WalletPassKind,
  candidate: unknown,
): WalletPassDesignDocument => {
  const result = validateWalletPassDesign(kind, candidate);
  if (!result.success)
    throw new InvalidWalletPassDesignError(kind, result.issues);
  return result.document;
};
